/**
 * Playlist Management API Service
 * This service handles communication with the Azure Functions backend for playlist functionality.
 */

import axios from 'axios';

class PlaylistService {
  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE;
    console.log('PlaylistService initialized with baseURL:', this.baseURL);
  }

  /**
   * Get all playlists for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response with playlists
   */
  async getUserPlaylists(userId) {
    try {
      const url = `${this.baseURL}/playlist/get?userId=${userId}`;
      console.log(`Getting playlists from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Operation completed successfully" };
    } catch (error) {
      console.error('Error fetching playlists:', error);
      throw error;
    }
  }

  /**
   * Get a specific playlist by ID
   * @param {string} userId - User ID
   * @param {string} playlistId - Playlist ID
   * @returns {Promise} - API response with playlist
   */
  async getPlaylist(userId, playlistId) {
    try {
      const url = `${this.baseURL}/playlist/get?userId=${userId}&id=${playlistId}`;
      console.log(`Getting playlist from: ${url}`);
      
      const response = await axios.get(url);
      
      if (response.data && response.data.data && response.data.data.length > 0) {
        return { data: response.data.data[0], message: "Playlist retrieved successfully" };
      }
      
      throw new Error('Playlist not found');
    } catch (error) {
      console.error('Error fetching playlist:', error);
      throw error;
    }
  }

  /**
   * Create a new playlist
   * @param {Object} playlistData - Playlist data object
   * @returns {Promise} - API response
   */
  async createPlaylist(playlistData) {
    try {
      const url = `${this.baseURL}/playlist/post`;
      console.log(`Creating playlist: ${url}`, playlistData);
      
      const response = await axios.post(url, playlistData);
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Playlist created successfully" };
    } catch (error) {
      console.error('Error creating playlist:', error);
      throw error;
    }
  }

  /**
   * Update a playlist
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @param {Object} updateData - Updated playlist data
   * @returns {Promise} - API response
   */
  async updatePlaylist(userId, docId, updateData) {
    try {
      const url = `${this.baseURL}/playlist/update`;
      console.log(`Updating playlist: ${url}`, { userId, docId, ...updateData });
      
      const response = await axios.put(url, { userId, docId, ...updateData });
      
      if (response.data && response.data.data) {
        return response.data;
      }
      
      return { data: response.data, message: "Playlist updated successfully" };
    } catch (error) {
      console.error('Error updating playlist:', error);
      throw error;
    }
  }

  /**
   * Delete a playlist
   * @param {string} userId - User ID
   * @param {string} docId - Document ID
   * @returns {Promise} - API response
   */
  async deletePlaylist(userId, docId) {
    try {
      const url = `${this.baseURL}/playlist/delete`;
      console.log(`Deleting playlist: ${url}`);
      
      const response = await axios.delete(url, {
        data: { userId, docId }
      });
      
      return response.data || { message: "Playlist deleted successfully" };
    } catch (error) {
      console.error('Error deleting playlist:', error);
      throw error;
    }
  }

  /**
   * Delete all playlists for a user
   * @param {string} userId - User ID
   * @returns {Promise} - API response
   */
  async deleteAllPlaylists(userId) {
    try {
      const url = `${this.baseURL}/playlist/deleteall`;
      console.log(`Deleting all playlists: ${url}`);
      
      const response = await axios.delete(url, {
        data: { userId }
      });
      
      return response.data || { message: "All playlists deleted successfully" };
    } catch (error) {
      console.error('Error deleting all playlists:', error);
      throw error;
    }
  }

}

const playlistService = new PlaylistService();
export default playlistService;
